<template>
  <scoped v-if="value">
    <EnumerationSelector
      v-if="deliverableEnumerationResponse"
      v-model:selection="value.type"
      title="Type"
      :options="deliverableEnumerationResponse.types"
    />
    <TextInput
      v-model:value="value.name"
      title="Name"
      :readOnly="readOnly"
    />
    <TextInput
      v-model:value="value.description"
      title="Description"
      :readOnly="readOnly"
      multiline
    />
    <TextInput
      v-model:value="value.ean"
      title="EAN"
      :readOnly="readOnly"
    />
    <TextInput
      v-model:value="value.memorandum"
      title="Memorandum"
      :readOnly="readOnly"
      multiline
    />
    <TextInput
      v-model:value="value.unit"
      title="Unit"
      :readOnly="readOnly"
    />
    <TextInput
      v-model:value="value.currency"
      title="Currency"
      :readOnly="readOnly"
    />
    <NumberInput
      v-model:value="grossUnitPrice"
      title="Gross Unit Price"
      :readOnly="readOnly"
      @input="updateNetUnitPrice()"
    />
    <NumberInput
      v-model:value="netUnitPrice"
      title="Net Unit Price"
      :readOnly="readOnly"
      @input="updateGrossUnitPrice()"
    />
    <NumberInput
      v-model:value="value.vatRate"
      title="VAT Rate"
      :readOnly="readOnly"
      @input="updateGrossUnitPrice()"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import NumberInput from "@/components/inputs/number.vue"
  import TextInput from "@/components/inputs/text.vue"
  import {computed, onBeforeMount, PropType, ref} from "vue"
  import {
    Deliverable,
    DeliverableEnumerationResponse,
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import EnumerationSelector from "@/components/selectors/enumeration.vue"
  import {Enumeration as EnumerationClient} from "@/endpoints/rutsolve/gql/resolvers/enumeration"
  import ErrorModal from "@/components/modals/error.vue"

  defineProps({
    readOnly: {
      default: false,
      required: false,
      type: Boolean
    }
  })

  const value = defineModel("value", {
    default: null,
    required: true,
    type: Object as PropType<Deliverable>
  })

  const error = ref<Error>(null)
  const enumerationClient = new EnumerationClient()
  const deliverableEnumerationResponse = ref<DeliverableEnumerationResponse>(null)

  const grossUnitPrice = computed({
    get() {
      if (!value.value || !value.value.unitPrice) {
        return null
      } else {
        return value.value.unitPrice.gross
      }
    }, set(input: number) {
      if (!value.value || !value.value.unitPrice) {
        value.value.unitPrice = {}
      }
      value.value.unitPrice.gross = input
    }
  })

  const netUnitPrice = computed({
    get() {
      if (!value.value || !value.value.unitPrice) {
        return null
      } else {
        return value.value.unitPrice.net
      }
    }, set(input: number) {
      if (!value.value || !value.value.unitPrice) {
        value.value.unitPrice = {}
      }
      value.value.unitPrice.net = input
    }
  })

  onBeforeMount( () => {
    read()
  })

  async function read() {
    try {
      deliverableEnumerationResponse.value = await enumerationClient.readDeliverableEnumerations()
    } catch(e) {
      error.value = e
    }
  }

  function updateGrossUnitPrice() {
    if (!value.value.unitPrice || !value.value.unitPrice.net || value.value.unitPrice.net == 0) {
      value.value.unitPrice.gross = 0
      return
    }
    if (!value.value.vatRate || value.value.vatRate == 0) {
      value.value.unitPrice.gross = value.value.unitPrice.net
      return
    }
    const cent = 100
    const vatRatio = (value.value.vatRate + cent) / cent
    value.value.unitPrice.gross = value.value.unitPrice.net * vatRatio
  }

  function updateNetUnitPrice() {
    if (!value.value.unitPrice || !value.value.unitPrice.gross || value.value.unitPrice.gross == 0) {
      value.value.unitPrice.net = 0
      return
    }
    if (!value.value.vatRate || value.value.vatRate == 0) {
      value.value.unitPrice.net = value.value.unitPrice.gross
      return
    }
    const cent = 100
    const vatRatio = (value.value.vatRate + cent) / cent
    value.value.unitPrice.net = value.value.unitPrice.gross / vatRatio
  }
</script>
